/**
 * Espelho de `whiz-server-2.0/src/modules/disparos/utils/substituir-variaveis-contato.util.ts`.
 *
 * Os dois lados precisam resolver as variáveis do mesmo jeito, então qualquer
 * mudança aqui tem que ser espelhada lá (e vice-versa).
 *
 * Dois usos, com pesos diferentes:
 *
 * - **agendamento** — PRÉ-VISUALIZAÇÃO. O texto é guardado CRU e o servidor
 *   substitui de novo no envio; aqui é só para o usuário conferir.
 * - **template escolhido no chat** — DEFINITIVO. O texto substituído cai no input,
 *   onde o operador ainda pode editá-lo antes de mandar, então o que aparece tem
 *   de ser exatamente o que sai. O servidor não substitui nada nesse caminho
 *   (mensagem de operador não leva a flag `substituirVariaveis`).
 *
 * Cobre apenas as duas variáveis de nome. As outras três do helper canônico de
 * atendimento (`{numero_protocolo}`, `{nome_canal}`, `{codigo}`) ficam de fora de
 * propósito.
 */

/** Contato mínimo para resolver as duas variáveis de nome. */
export interface ContatoVariaveis {
    nome?: string | null
    /**
     * Nome do perfil do WhatsApp. No banco é `@default("")`, então string vazia é
     * tão "ausente" quanto `null` e cai no nome cadastrado.
     */
    nomeWhatsapp?: string | null
}

/** As variáveis de nome oferecidas na UI, na ordem em que aparecem. */
export const VARIAVEIS_AGENDAMENTO = ['{nome_contato}', '{nome_whatsapp}'] as const

/**
 * Valor de `{nome_whatsapp}`: nome do perfil do WhatsApp, caindo para o nome
 * cadastrado quando o contato não tem (ou nunca teve) perfil conhecido.
 */
export function nomeWhatsappDeContato(contato: ContatoVariaveis): string {
    return contato.nomeWhatsapp?.trim() || (contato.nome ?? '')
}

/**
 * Substitui as variáveis de nome no texto.
 *
 * Sem contato (agendamento de grupo, ou contato ainda não carregado no store) o
 * texto volta intocado — mostrar a chave é mais honesto que mostrar vazio, porque
 * é exatamente o que o grupo vai receber.
 */
export function substituirVariaveisContato(
    contato: ContatoVariaveis | null | undefined,
    mensagem?: string | null,
): string {
    if (!mensagem) return ''
    if (!contato?.nome) return mensagem

    return mensagem
        .replaceAll('{nome_contato}', contato.nome)
        .replaceAll('{nome_whatsapp}', nomeWhatsappDeContato(contato))
}

/**
 * Há alguma variável de nome no texto? Decide se o preview aparece.
 *
 * Só o front precisa desta pergunta (o servidor sempre chama o `replaceAll`, que já
 * é no-op sem variável), mas a lista tem que acompanhar os `replaceAll` acima: quem
 * acrescentar uma variável lá acrescenta aqui.
 */
export function temVariaveisDeNome(texto?: string | null): boolean {
    if (!texto) return false
    return VARIAVEIS_AGENDAMENTO.some(v => texto.includes(v))
}

/**
 * Quais variáveis de nome o texto usa mas este contato não consegue preencher.
 *
 * Existe porque o caminho do chat é definitivo: `substituirVariaveisContato`
 * devolve o texto intocado quando não há contato, e sem esta lista o operador
 * mandaria `{nome_contato}` cru para o cliente sem nunca ter sido avisado.
 *
 * As duas variáveis caem juntas de propósito: `{nome_whatsapp}` já cai para o
 * nome cadastrado, então é `contato.nome` que decide se alguma delas resolve.
 */
export function variaveisDeNomeNaoResolvidas(
    contato: ContatoVariaveis | null | undefined,
    texto?: string | null,
): string[] {
    if (!texto) return []
    if (contato?.nome) return []
    return VARIAVEIS_AGENDAMENTO.filter(v => texto.includes(v))
}
